import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ContractSelectionModal from './ContractSelectionModal';
import { 
  CircularProgress, 
  Box, 
  Typography,
  Container,
  Paper
} from '@mui/material';

const ClientLanding = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && user.role === 'client') {
      fetchUserContracts();
    }
  }, [user]);

  const fetchUserContracts = async () => {
    try {
      setLoading(true);
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://pulse.marketersindemand.com';
      
      const response = await fetch(`${apiBaseUrl}/api/users/${user.id}/contracts`);
      if (!response.ok) {
        throw new Error(`Failed to fetch contracts: ${response.status}`);
      }
      
      const contractData = await response.json();
      console.log('Client contracts:', contractData);
      
      setContracts(contractData);
      
      // Handle contract selection logic
      if (contractData.length === 0) {
        setError('No contracts assigned to your account. Please contact your administrator.');
      } else if (contractData.length === 1) {
        // Auto-navigate to the single contract
        console.log('Single contract found, auto-navigating to:', contractData[0].contractId);
        navigate(`/client-view/${contractData[0].contractId}/dashboard`);
      } else {
        // Show modal for multiple contracts
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching user contracts:', error);
      setError(`Failed to load your contracts: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleContractSelect = (contractId) => {
    console.log('Contract selected:', contractId);
    setShowModal(false);
    navigate(`/client-view/${contractId}/dashboard`);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress style={{ color: '#8A42EC' }} />
        <Typography variant="body1" color="textSecondary">
          Loading your contracts...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            backgroundColor: '#fafafa'
          }}
        >
          <Typography variant="h5" gutterBottom color="error">
            Access Issue
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            If you believe this is an error, please contact your account manager or administrator.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        gap={2}
      >
        <Typography variant="h5" gutterBottom>
          Welcome to Pulse
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Please select your contract to continue...
        </Typography>
      </Box>

      {showModal && (
        <ContractSelectionModal
          contracts={contracts}
          onClose={handleCloseModal}
          onSelectContract={handleContractSelect}
          title="Select Your Contract"
          description="Please select the contract you would like to view:"
        />
      )}
    </>
  );
};

export default ClientLanding; 